/**
 * Judge.me review dates: show the stored calendar date in every timezone.
 *
 * Why: Judge.me's v3 widget formats `created_at` with local-time getters
 * (`new Date(iso).getDate()`). The 2,014 reviews imported from Stamped on 2026-10-23 carry a
 * date with no time of day, which Judge.me stores as midnight UTC
 * ("2024-11-08T00:00:00.000Z"). In any timezone west of UTC that instant is still the previous
 * evening, so every US visitor saw each imported review one day early (Judge.me admin shows the
 * right date).
 *
 * Fix: before the widget reads its data, rewrite only values that are EXACTLY midnight UTC as a
 * wall-clock midnight with no offset ("2024-11-08T00:00:00"). ECMAScript parses an offset-less
 * date-time as local time, so the widget prints the stored date wherever the visitor is.
 * Reviews with a real time of day (every organic review) are left untouched.
 *
 * Two data paths are covered:
 *  1. First page: inline `jdgm.data.*` payloads rendered by the Judge.me app blocks. This file is
 *     loaded WITHOUT defer just before </body>, so it runs after those payloads exist and before
 *     Judge.me's deferred loader starts the widget.
 *  2. Later pages, sorting and filtering: JSON fetched from *.judge.me. window.fetch is wrapped for
 *     judge.me hosts only; any other request, a non-JSON response or any parse error passes the
 *     original response through unchanged.
 *
 * Remove this file (and its two script tags) once Judge.me formats dates in UTC, or once the
 * imported reviews carry a time of day. Verify with a US timezone before removing.
 */
(function () {
  'use strict';

  var MIDNIGHT_UTC = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.0+)?(?:Z|\+00:00)$/;
  var DATE_KEYS = { created_at: true, date: true };
  var MAX_DEPTH = 10;

  function normalise(node, depth) {
    if (!node || typeof node !== 'object' || depth > MAX_DEPTH) return;
    if (Array.isArray(node)) {
      for (var i = 0; i < node.length; i++) normalise(node[i], depth + 1);
      return;
    }
    for (var key in node) {
      if (!Object.prototype.hasOwnProperty.call(node, key)) continue;
      var value = node[key];
      if (typeof value === 'string') {
        if (DATE_KEYS[key]) {
          var match = MIDNIGHT_UTC.exec(value);
          if (match) node[key] = match[1] + 'T00:00:00';
        }
      } else if (value && typeof value === 'object') {
        normalise(value, depth + 1);
      }
    }
  }

  window.jdgmDateFix = { normalise: normalise };

  try {
    if (window.jdgm && window.jdgm.data) normalise(window.jdgm.data, 0);
  } catch (e) {
    /* never break the page over a date label */
  }

  if (typeof window.fetch !== 'function' || window.fetch.jdgmDateFix) return;

  var JUDGEME_HOST = /^https?:\/\/([a-z0-9-]+\.)*judge\.me(\/|$)/i;
  var originalFetch = window.fetch;

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    if (input && typeof input.href === 'string') return input.href;
    return '';
  }

  function patchedFetch(input) {
    var pending = originalFetch.apply(this, arguments);
    if (!JUDGEME_HOST.test(requestUrl(input))) return pending;

    return pending.then(function (response) {
      var type = (response.headers && response.headers.get('content-type')) || '';
      if (!response.ok || type.indexOf('json') === -1) return response;

      return response
        .clone()
        .text()
        .then(function (text) {
          if (text.indexOf('T00:00:00') === -1) return response;
          var data = JSON.parse(text);
          normalise(data, 0);
          var headers = new Headers(response.headers);
          headers.delete('content-length');
          headers.delete('content-encoding');
          return new Response(JSON.stringify(data), {
            status: response.status,
            statusText: response.statusText,
            headers: headers
          });
        })
        .catch(function () {
          return response;
        });
    });
  }

  patchedFetch.jdgmDateFix = true;
  window.fetch = patchedFetch;
})();
